/**
 * The summary figures for a page, as one object rather than a row of boxes.
 *
 * A grid of bordered stat cards spends four borders, four hover states and four
 * icon tiles on what is a single sentence: "across this scope there are 6 MSPs,
 * 18 companies and 54 users, spending $1.9M". Each box also has to be wide
 * enough for its longest label, so a row of them is mostly empty space, and the
 * table underneath — the thing people actually came to read — gets pushed below
 * the fold.
 *
 * One hairline container, hairline dividers between items, no per-item chrome.
 * The numbers stay the same size they always were; what goes away is the
 * packaging around them.
 *
 * This is deliberately *not* the dashboard `StatCard`. That one is a standalone
 * tile with a hover state and an icon, appropriate when a metric is a link or
 * sits alone. This is for a fixed set of read-only figures that belong together.
 */

import type { ReactNode } from "react";

import { DeltaChip, type DeltaSentiment } from "./DeltaChip";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export interface MetricStripItem {
  label: string;
  value: ReactNode;
  /** Optional qualifier, e.g. "of 18 in scope". Kept to a few words. */
  hint?: string;
  /**
   * Period-over-period change as a **fraction**: `0.279` is +27.9%.
   *
   * Shares `DeltaChip` with `KpiCard` rather than restating the arrow and the
   * colour rule, because the rule is easy to get backwards: on spend a rise is
   * the bad outcome, so the chip defaults to treating a fall as good. Omit the
   * key entirely for a figure with no comparison; pass `null` when there is a
   * comparison to make and no baseline to make it against.
   */
  change?: number | null;
  sentiment?: DeltaSentiment;
}

/**
 * Column count follows the item count.
 *
 * A fixed `sm:grid-cols-4` left an empty cell whenever a page passed three
 * metrics — the strip then had a visible gap on its right-hand end that looked
 * like a metric had failed to load. Enumerated rather than interpolated because
 * Tailwind scans source text for class names and cannot see `grid-cols-${n}`.
 */
const COLS: Record<number, string> = {
  1: "sm:grid-cols-1",
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-3",
  4: "sm:grid-cols-4",
  5: "sm:grid-cols-5",
  6: "sm:grid-cols-6",
};

export function MetricStrip({
  items,
  className,
}: {
  items: MetricStripItem[];
  className?: string;
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border/60 bg-border/60",
        COLS[items.length] ?? "sm:grid-cols-4",
        className,
      )}
    >
      {items.map((item) => (
        // Each cell paints its own background; the 1px grid gap shows the
        // container's colour through, which is what draws the dividers. This
        // survives wrapping — a `divide-x` would leave a stray rule at the
        // start of the second row.
        <div key={item.label} className="bg-card px-4 py-3">
          {/* Label and hint wrap rather than trim: a strip cell is narrow
              at four across, and "Nothing billed the p…" is not a fact. */}
          <p className="text-xs font-medium text-muted-foreground">
            {item.label}
          </p>
          {/* A div: the value is any node, and a badge or tooltip inside a
              <p> is invalid HTML (React warns on every render). */}
          <div className="mt-1 text-xl font-semibold tabular-nums tracking-tight">
            {item.value}
          </div>
          {item.change !== undefined ? (
            <DeltaChip
              value={item.change ?? null}
              sentiment={item.sentiment}
              className="mt-0.5"
            />
          ) : null}
          {item.hint ? (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {item.hint}
            </p>
          ) : null}
        </div>
      ))}
    </div>
  );
}


/**
 * The strip's loading state, in the strip's own shape.
 *
 * It lives here rather than in `common/skeletons` for the reason
 * `StatCardsSkeleton` mirrors `StatCard`: a placeholder only does its job while
 * it matches the thing it stands in for, and the surest way to keep the two
 * agreeing is to keep them in one file. Every consumer was drawing its own —
 * `h-20 w-full` plain blocks in two cost pages, a bordered card grid in the
 * router fallback — so the page loaded as separated boxes and then *rebuilt
 * itself* as one joined strip. A skeleton that changes shape on load is worse
 * than none: it reports a layout that never existed.
 *
 * The container and cells are the same classes the real strip uses, so the two
 * cannot drift without this file changing.
 */
export function MetricStripSkeleton({
  count = 4,
  /** Reserve the qualifier line. Pass false for a strip with no hints. */
  hints = true,
  className,
}: {
  count?: number;
  hints?: boolean;
  className?: string;
}) {
  return (
    <div
      aria-busy="true"
      aria-label="Loading figures"
      className={cn(
        "grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border/60 bg-border/60",
        COLS[count] ?? "sm:grid-cols-4",
        className,
      )}
    >
      {Array.from({ length: count }).map((_, i) => (
        // Same box model as a real cell (px-4 py-3, 12/20/12px type stack), so
        // the row does not change height when the figures arrive.
        <div key={i} className="bg-card px-4 py-3">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="mt-1 h-7 w-24" />
          {hints ? <Skeleton className="mt-0.5 h-4 w-16" /> : null}
        </div>
      ))}
    </div>
  );
}
